/**
 * Pure helpers for Home's "Pipeline by team" block: which days a period
 * covers, the period it's compared with, how a change reads, and where each
 * count links. Kept free of React so the date maths is unit-tested.
 *
 * Periods are whole UTC days ending with today. UTC because the Invites page
 * sends its `YYYY-MM-DD` dates to the backend as UTC midnights, so a count
 * here and the list it links to cover the same invites. Whole days also keep
 * the query key stable for the day instead of changing every render.
 */
import type { TeamPipelineRow } from '@/api/types';
import { COUNT_STATUSES, invitesListHref, ratio } from './chart';
import { invitesBaseForTeam, teamSection } from './teams';

const DAY = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Periods ───────────────────────────────────────────────────────────────

export type PipelinePeriod = 'week' | 'lastWeek' | '30d';

interface PeriodDef {
  value: PipelinePeriod;
  label: string;
  /** Length in days. */
  days: number;
  /** How many days before today the period ends. */
  endsDaysAgo: number;
}

export const PIPELINE_PERIODS: readonly PeriodDef[] = [
  { value: 'week', label: 'This week', days: 7, endsDaysAgo: 0 },
  { value: 'lastWeek', label: 'Last week', days: 7, endsDaysAgo: 7 },
  { value: '30d', label: '30 days', days: 30, endsDaysAgo: 0 },
];

export const DEFAULT_PIPELINE_PERIOD: PipelinePeriod = 'week';

/** `?period=` from the URL, or the default when absent or unknown. */
export function parsePipelinePeriod(raw: string | null | undefined): PipelinePeriod {
  return PIPELINE_PERIODS.some((p) => p.value === raw) ? (raw as PipelinePeriod) : DEFAULT_PIPELINE_PERIOD;
}

/** Half-open range: `from` is included, `to` is not. Both are UTC midnights. */
export interface DayRange {
  from: Date;
  to: Date;
}

function startOfUtcDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * The period and the equivalent one before it. "This week" is today and the
 * six days before; "Last week" is the seven days before that; "30 days" is
 * today and the 29 before. The previous period is the same length, directly
 * before.
 */
export function pipelineRanges(
  period: PipelinePeriod,
  now: Date = new Date(),
): { current: DayRange; previous: DayRange } {
  const def = PIPELINE_PERIODS.find((p) => p.value === period) ?? PIPELINE_PERIODS[0]!;
  const end = startOfUtcDay(now) + DAY - def.endsDaysAgo * DAY;
  const start = end - def.days * DAY;
  return {
    current: { from: new Date(start), to: new Date(end) },
    previous: { from: new Date(start - def.days * DAY), to: new Date(start) },
  };
}

/** Query params for the team-pipeline endpoint. */
export function pipelineQueryParams(r: DayRange): { dateFrom: string; dateTo: string } {
  return { dateFrom: r.from.toISOString(), dateTo: r.to.toISOString() };
}

const day = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * The range as Invites-page dates. The backend reads `dateTo` as "created at
 * or before that UTC midnight", so the exclusive end goes in as-is.
 */
export function pipelineLinkDates(r: DayRange): { dateFrom: string; dateTo: string } {
  return { dateFrom: day(r.from), dateTo: day(r.to) };
}

/** "25 Sep – 1 Oct 2026": the days a range covers, last day inclusive. */
export function formatDayRange(r: DayRange): string {
  const last = new Date(r.to.getTime() - DAY);
  const d = (x: Date) => `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]}`;
  const left = r.from.getUTCFullYear() === last.getUTCFullYear() ? d(r.from) : `${d(r.from)} ${r.from.getUTCFullYear()}`;
  return `${left} – ${d(last)} ${last.getUTCFullYear()}`;
}

// ── Changes ───────────────────────────────────────────────────────────────

export type DeltaTone = 'up' | 'down' | 'flat';

export interface Delta {
  text: string;
  tone: DeltaTone;
}

/** A count's change on the previous period: "+12", "−3", "0". */
export function countDelta(current: number, previous: number): Delta {
  const diff = current - previous;
  if (diff === 0) return { text: '0', tone: 'flat' };
  return diff > 0
    ? { text: `+${diff.toLocaleString()}`, tone: 'up' }
    : { text: `−${Math.abs(diff).toLocaleString()}`, tone: 'down' };
}

/** The change as a percentage of the previous period, or null from zero. */
export function percentDelta(current: number, previous: number): string | null {
  if (previous <= 0) return null;
  const pctChange = Math.round(((current - previous) / previous) * 100);
  if (pctChange === 0) return '0%';
  return pctChange > 0 ? `+${pctChange}%` : `−${Math.abs(pctChange)}%`;
}

/** A rate's change in percentage points ("+3.1 pts"), or null when either side has no rate. */
export function rateDelta(current: number | null, previous: number | null): Delta | null {
  if (current === null || previous === null) return null;
  const pts = Math.round((current - previous) * 1000) / 10;
  if (pts === 0) return { text: '0 pts', tone: 'flat' };
  return pts > 0 ? { text: `+${pts.toFixed(1)} pts`, tone: 'up' } : { text: `−${Math.abs(pts).toFixed(1)} pts`, tone: 'down' };
}

// ── Join dates ────────────────────────────────────────────────────────────

/**
 * Join dates (`verified_at`) are only recorded from this UTC day. Joins
 * before it have no date, so "joined this period" undercounts any range
 * that starts earlier, and a comparison with such a range means nothing.
 */
export const JOIN_DATES_FROM = new Date(Date.UTC(2026, 9, 1));
export const JOIN_DATES_NOTE = 'Join dates are only recorded from 1 Oct 2026, so joins before then aren’t counted.';

/** True when every day of the range is on or after JOIN_DATES_FROM. */
export function joinDatesCover(r: DayRange): boolean {
  return r.from.getTime() >= JOIN_DATES_FROM.getTime();
}

/** Reply → join rate: joined ÷ invites logged, null without invites. */
export function joinRate(row: Pick<TeamPipelineRow, 'joined' | 'invites'> | undefined): number | null {
  return row ? ratio(row.joined, row.invites) : null;
}

// ── Rows ──────────────────────────────────────────────────────────────────

/** Joins and posts on this team are entered by hand, so they aren't shown as tracked. */
export const HAND_TRACKED_TEAMS: ReadonlySet<string> = new Set(['INFLUENCER']);

export interface PipelineTeamRow {
  team: string;
  displayName?: string;
  current: TeamPipelineRow;
  previous: TeamPipelineRow;
  /** False when the team recorded no messages in either period. */
  recordsMessages: boolean;
  /** False for teams whose joins and posts are updated by hand. */
  tracksJoins: boolean;
}

const EMPTY = (team: string): TeamPipelineRow => ({
  team,
  messages: 0,
  invites: 0,
  joined: 0,
  posted: 0,
  joinedInPeriod: 0,
});

/**
 * Pair each team's current and previous rows. `team` ('' = all) keeps only
 * that team. Sorted by invites logged this period, then by code.
 */
export function pairPipelineRows(
  current: readonly TeamPipelineRow[],
  previous: readonly TeamPipelineRow[],
  team = '',
): PipelineTeamRow[] {
  const prevBy = new Map(previous.map((r) => [r.team, r]));
  const codes = new Set([...current.map((r) => r.team), ...previous.map((r) => r.team)]);
  const out: PipelineTeamRow[] = [];
  for (const code of codes) {
    if (team && code !== team) continue;
    const cur = current.find((r) => r.team === code) ?? EMPTY(code);
    const prev = prevBy.get(code) ?? EMPTY(code);
    out.push({
      team: code,
      displayName: cur.teamDisplayName ?? prev.teamDisplayName,
      current: cur,
      previous: prev,
      recordsMessages: cur.messages > 0 || prev.messages > 0,
      tracksJoins: !HAND_TRACKED_TEAMS.has(code),
    });
  }
  return out.sort((a, b) => b.current.invites - a.current.invites || a.team.localeCompare(b.team));
}

export interface PipelineTotals {
  messages: number;
  invites: number;
  /** Joins that happened in the period (`joinedInPeriod`, by join date). */
  joined: number;
  /** First posts in the period (by post date). */
  posted: number;
}

/**
 * Totals across teams for one side ('current' / 'previous'). Joined and
 * posted are events in the period (not "of the invites logged then"), and
 * leave out hand-tracked teams, matching the "Not tracked" cells.
 */
export function pipelineTotals(rows: readonly PipelineTeamRow[], side: 'current' | 'previous'): PipelineTotals {
  const t: PipelineTotals = { messages: 0, invites: 0, joined: 0, posted: 0 };
  for (const r of rows) {
    const v = r[side];
    t.messages += v.messages;
    t.invites += v.invites;
    if (r.tracksJoins) {
      t.joined += v.joinedInPeriod;
      t.posted += v.posted;
    }
  }
  return t;
}

// ── Links ─────────────────────────────────────────────────────────────────

export interface PipelineLinks {
  messages: string;
  invites: string;
  joined: string;
  posted: string;
}

/**
 * Where each of a team's counts opens, filtered on the same date the count
 * uses: invites by when they were logged, joined by join date
 * (`verifiedFrom/To`), posted by first-post date (`convertedFrom/To`).
 * Teams with their own section (Influencer, Vyral) open there, which pins
 * the team itself.
 */
export function pipelineLinks(team: string, range: DayRange): PipelineLinks {
  const { dateFrom, dateTo } = pipelineLinkDates(range);
  const section = teamSection(team);
  const base = invitesBaseForTeam(team);
  const scopedTeam = section ? null : team;
  return {
    messages: invitesListHref({ base, view: 'contacted', team: scopedTeam }),
    invites: invitesListHref({ base, team: scopedTeam, dateFrom, dateTo, statuses: COUNT_STATUSES.logged }),
    joined: invitesListHref({
      base,
      team: scopedTeam,
      verifiedFrom: dateFrom,
      verifiedTo: dateTo,
      statuses: COUNT_STATUSES.joined,
    }),
    posted: invitesListHref({
      base,
      team: scopedTeam,
      convertedFrom: dateFrom,
      convertedTo: dateTo,
      statuses: COUNT_STATUSES.posted,
    }),
  };
}
